/**
 * FinanzenFirmaNav — Firma-Untermenü im Finanzen-Cockpit
 *
 * Zweite Spalte (analog AktenProzessSpalte): zeigt die Sektionen der aktuell
 * gewählten Finanzen-Firma (SV Büro / LCS / Immobilien / Alle). Quelle der
 * Sektionen ist FINANZEN_FIRMA_NAV aus lib/navigation.
 *
 * - DESKTOP: vertikale Spalte (helles Design, farbiger Trennbalken oben in
 *   der Firmenfarbe, aktiver Eintrag in der Firmenfarbe hervorgehoben).
 * - MOBILE: horizontale, scrollbare Tab-Leiste.
 *
 * Navigation: setLocation(`/finanzen/${firma}/${section}`).
 */

import { useLocation } from 'wouter';
import {
  getFinanzenFirma, getFinanzenSektionen,
  type FinanzSektion, type FinanzenFirmaId,
} from '@/lib/navigation';
import {
  BarChart3, ArrowDownToLine, ArrowUpFromLine, CalendarRange, FileText, Car,
  Receipt, CreditCard, Scale, Wallet, Download, Building2, TrendingDown,
  Banknote, Layers, FileText as FileIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  BarChart3, ArrowDownToLine, ArrowUpFromLine, CalendarRange, FileText, Car,
  Receipt, CreditCard, Scale, Wallet, Download, Building2, TrendingDown,
  Banknote, Layers,
};

interface FinanzenFirmaNavProps {
  firmaId: FinanzenFirmaId;
  /** Aktive Sektion (id). */
  section: string;
  mobile?: boolean;
}

export default function FinanzenFirmaNav({ firmaId, section, mobile }: FinanzenFirmaNavProps) {
  const [, setLocation] = useLocation();
  const firma = getFinanzenFirma(firmaId);
  const sektionen = getFinanzenSektionen(firmaId);

  const handleClick = (item: FinanzSektion) => {
    setLocation(`/finanzen/${firmaId}/${item.id}`);
  };

  // ===== MOBILE: horizontale scrollbare Leiste =====
  if (mobile) {
    return (
      <div className="flex items-center gap-1 px-3 py-2 overflow-x-auto scrollbar-hide bg-gray-50 border-b border-gray-200">
        {sektionen.map((item) => {
          const Icon = ICON_MAP[item.icon] || FileIcon;
          const isActive = section === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              data-testid={`finanzen-nav-${firmaId}-${item.id}`}
              className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                isActive ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              style={isActive ? { backgroundColor: firma.farbe } : undefined}
            >
              <Icon className="w-3 h-3" />
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  // ===== DESKTOP: vertikale Spalte — helles Design =====
  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Farbiger Trennbalken oben (4px) in Firmenfarbe */}
      <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: firma.farbe }} />

      {/* Header: Firma */}
      <div className="px-3 py-3 border-b border-gray-200 flex-shrink-0 bg-white">
        <div className="px-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 block">
            Firma
          </span>
          <span className="text-[13px] font-bold text-gray-800 block truncate mt-0.5">
            {firma.label}
          </span>
        </div>
      </div>

      {/* Sektionen */}
      <nav
        className="flex-1 overflow-y-auto py-2 px-2"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.1) transparent' }}
      >
        <div className="px-2 pt-1 pb-2">
          <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">
            Bereiche
          </span>
        </div>

        {sektionen.map((item) => {
          const Icon = ICON_MAP[item.icon] || FileIcon;
          const isActive = section === item.id;
          const geplant = item.status === 'geplant';
          return (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              data-testid={`finanzen-nav-${firmaId}-${item.id}`}
              className={`w-full flex items-center gap-2.5 rounded-lg mb-0.5 px-2.5 py-2 transition-all duration-150 ${
                isActive
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200 active:bg-gray-300'
              }`}
              style={isActive ? { backgroundColor: firma.farbe } : undefined}
              title={item.label}
            >
              <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
              <span className="text-[13px] font-medium truncate flex-1 text-left">{item.label}</span>
              {geplant && !isActive && (
                <span className="text-[8px] font-semibold uppercase tracking-wide text-gray-300 flex-shrink-0">
                  geplant
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
